/**
 * AgileVision.io DCK entry point.
 * This is the entry point of the whole React.js application that contains 
 * declarations of available routes, Redux store bootstrapping code and
 * react-router-redux configuration.
 * 
 * NOTE: In case of hosting on CDNs that doesn't support URL rewrites,
 * additional browser history-related magic should be done in this file.
 */

import React from "react"
import ReactDOM from "react-dom"
import { Provider } from "react-redux"
import {
  Router,
  Route,
  IndexRedirect,
  IndexRoute,
  browserHistory
} from "react-router"
import { syncHistoryWithStore } from "react-router-redux"
import "bootstrap/dist/css/bootstrap.css"
import "font-awesome/css/font-awesome.min.css"
import { DckActionCreators } from "dck-redux"

import App from "./components/App"
import SignIn from "./components/SignIn"

import Users from "./components/Users"
import ListUsers from "./components/Users/List"
import EditUser from "./components/Users/EditUser"
import AddUser from "./components/Users/AddUser"

import NotFound from "./components/NotFound"
import Home from "./components/Home"
import Update from "./components/Update"

import "./index.css"

import registerServiceWorker from "./registerServiceWorker"

import Customers from "./components/Customers/index"
import ListCustomers from "./components/Customers/List"
import DetailCustomer from "./components/Customers/Detail"

import Loans from "./components/Loans/index"
import ListLoans from "./components/Loans/List"
import DetailLoan from "./components/Loans/Detail"

import Bundles from "./components/Bundles/index"
import ListBundles from "./components/Bundles/List"
import AddBundle from "./components/Bundles/Add"
import DetailBundle from "./components/Bundles/Detail"
import UpdateBundle from "./components/Bundles/Update"
import store from "./store"
import { ActionCreators } from "./redux/actions"
import Batches from "./components/Batches/index"
import ListBatches from "./components/Batches/List"
import Import from "./components/Import/"
import Repayment from "./components/Loans/Repayment"
import Settings from "./components/Settings"
import HomeSettings from "./components/Settings/HomeSettings"
import AgeSettings from "./components/Settings/AgeSettings"
import Organizations from "./components/Organizations"
import ListOrganizations from "./components/Organizations/OrganizationList"
import OrganizationDetail from "./components/Organizations/OrganizationDetail"
import CreateNewOrganization from "./components/Organizations/CreateNewOrganization"
import OrganizationLocked from "./components/Organizations/OrganizationLocked"
import UpdateOrganization from "./components/Organizations/UpdateOrganization"
import currentstore from "store"
import UpdatePassword from "./components/Users/EditUser/UpdatePassword"
import TermsAndConditions from "./components/Settings/TermsAndConditions"
import MyAccount from "./components/MyAccount"
import LoanVerification from "./components/Settings/LoanVerification"
import CustomerDataSettings from "./components/Settings/Customers"
import QRcodes from "./components/Settings/QRcodes"
import Reports from "./components/Reports"
import ReportsList from "./components/Reports/ReportsList"
import ActiveLoanReport from "./components/Reports/ActiveLoanReport"
import ExpectedPaymentReport from "./components/Reports/ExpectedPaymentReport"
import BalanceOutStandingReport from "./components/Reports/BalanceOutstandingReport"
import AgingLoanReport from "./components/Reports/AgingLoanReport"
import PortfolioAtRiskReport from "./components/Reports/PortfolioAtRiskReport"
import Authenticated from "./lib/Authenticated"
import Businesses from "./components/Business"
import ListBusinesses from "./components/Business/List"
import DetailBusiness from "./components/Business/Detail"
import BatchRepayment from "./components/Loans/BatchRepayment"
import CreateBusiness from "./components/Business/CreateBusiness"
import UpdateBusiness from "./components/Business/UpdateBusiness"
import BranchCashInReport from "./components/Reports/BranchCashInReport"
import Test from "./components/App/Test"
import LoanGeneralityReport from "./components/Reports/LoanGeneralityReport"
import Charges from "./components/Charges"
import CreateCharge from "./components/Charges/CreateCharge"
import ListCharges from "./components/Charges/List"
import UpdateCharge from "./components/Charges/UpdateCharge"
import DetailCharge from "./components/Charges/DetailCharge.js"
import Accounting from "./components/Accounting"
import AccountingMenu from "./components/Accounting/AccountingMenu"
import GLAccount from "./components/Accounting/GLAccounts"
import ListGLAccount from "./components/Accounting/GLAccounts/List"
import CreateGLAccount from "./components/Accounting/GLAccounts/Create"
import GLAccountDetails from "./components/Accounting/GLAccounts/Details"
import UpdateGLAccount from "./components/Accounting/GLAccounts/Update"
import JournalEntry from "./components/Accounting/JournalEntries"
import ListJournalEntries from "./components/Accounting/JournalEntries/List"
import CreateJournalEntry from "./components/Accounting/JournalEntries/Create"
import JournalEntryDetails from "./components/Accounting/JournalEntries/Details"
import GLClosure from "./components/Accounting/GLClosures"
import CreateGLClosure from "./components/Accounting/GLClosures/Create"
import GLClosureDetails from "./components/Accounting/GLClosures/Details"
import UpdateGLClosure from "./components/Accounting/GLClosures/Update"
import ListGLClosure from "./components/Accounting/GLClosures/List"
import AccountingRule from "./components/Accounting/AccountingRules"
import ListAccountingRules from "./components/Accounting/AccountingRules/List"
import AccountingRuleDetails from "./components/Accounting/AccountingRules/Details"
import UpdateAccountingRule from "./components/Accounting/AccountingRules/Update"
import CreateAccountingRule from "./components/Accounting/AccountingRules/Create"
import GeneralLedgerReport from "./components/Reports/GeneralLedgerReport"
import IncomeStatementReport from "./components/Reports/IncomeStatementReport"
import BalanceSheetReport from "./components/Reports/BalanceSheetReport"
import TrialBalanceReport from "./components/Reports/TrialBalanceReport"
import BaseCurrency from "./components/Settings/BaseCurrency"
import LoanAccounting from "./components/Accounting/LoanAccounting"
import LoanAccountingDetails from "./components/Accounting/LoanAccounting/Details"
import CreateLoanAccounting from "./components/Accounting/LoanAccounting/Create"
import LoanCommission from "./components/Accounting/LoanCommission"
import UpdateLoanCommission from "./components/Accounting/LoanCommission/Update"
import ListLoanCommission from "./components/Accounting/LoanCommission/List"
import LoanCommissionDetails from "./components/Accounting/LoanCommission/Details"
import CreateLoanCommission from "./components/Accounting/LoanCommission/Create"
import ExtraAccounting from "./components/Accounting/ExtraAccounting"
import ExtraLoanAccountingDetails from "./components/Accounting/ExtraAccounting/Details"
import CreateExtraLoanAccounting from "./components/Accounting/ExtraAccounting/Create"
import CustomAccountingRule from "./components/Accounting/CustomAccountingRules"
import CustomAccountingRuleDetails from "./components/Accounting/CustomAccountingRules/Details"
import CreateCustomAccountingRule from "./components/Accounting/CustomAccountingRules/Create"
import LoanDisbursedReport from "./components/Reports/LoanDisbursedReport"
import Financiers from "./components/Financiers"
import CreateFinancierBatch from "./components/Financiers/Batches/Create"
import FinancierBatchDetails from "./components/Financiers/Batches/Details"
import UpdateFinancierBatch from "./components/Financiers/Batches/Update"
import FinancierBatches from "./components/Financiers/Batches"
import ListFinancierBatches from "./components/Financiers/Batches/List"
import FinancierBuckets from "./components/Financiers/Buckets"
import CreateFinancierBucket from "./components/Financiers/Buckets/Create"
import FinancierBucketDetails from "./components/Financiers/Buckets/Details"
import UpdateFinancierBucket from "./components/Financiers/Buckets/Update"
import ListFinancierBuckets from "./components/Financiers/Buckets/List"
import Investors from "./components/Financiers/Investors"
import ListInvestors from "./components/Financiers/Investors/List"
import BatchPerformanceReport from "./components/Reports/financiers/BatchPerformance"
import BucketPerformanceReport from "./components/Reports/financiers/BucketPerformance"
import UpdateInvestorPayment from "./components/Financiers/Investors/Payments/Update"
import CreateInvestorPayment from "./components/Financiers/Investors/Payments/Create"
import InvestorPaymentDetails from "./components/Financiers/Investors/Payments/Details"
import ListInvestorPayments from "./components/Financiers/Investors/Payments/List"
import CreateInvestor from "./components/Financiers/Investors/Create"
import UpdateInvestor from "./components/Financiers/Investors/Update"
import InvestorDetails from "./components/Financiers/Investors/Details"
import AccountingSetting from "./components/Settings/AccountingSetting"
import DefaultedLoanAccounting from "./components/Accounting/DefaultedLoanAccounting"
import DefaultedLoanAccountingDetails from "./components/Accounting/DefaultedLoanAccounting/Details"
import CreateDefaultedLoanAccounting from "./components/Accounting/DefaultedLoanAccounting/Create"
import DefaultedLoanReport from "./components/Reports/DefaultedLoanReport"
import Notifications from "./components/Notifications/index.js"
import NotificationList from "./components/Notifications/List.js"
// import VATAccountingRule from "./components/Accounting/VATAccountingRule"
// import VATAccountingRuleDetails from "./components/Accounting/VATAccountingRule/Details"
// import CreateVATAccountingRule from "./components/Accounting/VATAccountingRule/Create"

const history = syncHistoryWithStore(browserHistory, store),

redirectIfNotSuperOrAdmin = () => {

  const authenticated = new Authenticated(),
  isSuper = authenticated.isSuper(),
  isAdmin = authenticated.isAdmin()

  if(!(isSuper || isAdmin)) {
    browserHistory.push("/home")
  }
},

redirectIfPaymentManager = () => {

  const authenticated = new Authenticated()

  if(authenticated.isPaymentManager()) {
    browserHistory.push("/home")
  }
},

canISeeReports = () => {

  const authenticated = new Authenticated(),
  isSuper = authenticated.isSuper(),
  isAdmin = authenticated.isAdmin(),
  isAccountant = authenticated.isAccountant(),
  isPaymentManager = authenticated.isPaymentManager()

  if(!(isSuper || isAdmin || isAccountant || isPaymentManager)) {
    browserHistory.push("/home")
  }
},

canISeeFinanciers = () => {

  const authenticated = new Authenticated(),
  isAdmin = authenticated.isAdmin(),
  isAccountant = authenticated.isAccountant()

  if(!(isAdmin || isAccountant)) {
    browserHistory.push("/home")
  }
}

ReactDOM.render(

  <Provider store={ store }>
    <Router history={ history }>

      <Route
        path="/sign-in"
        component={ SignIn }
        onEnter={() => store.dispatch(ActionCreators.checkNotAuthenticated())}
      />

      <Route
        path="/update-password/:userId"
        component={ UpdatePassword }
        onEnter={() => {

          const sessionData = currentstore.get("sessionData")
          if(!sessionData || !sessionData.access_token) {
            browserHistory.push("/sign-in")
          }
        }}
      />

      <Route path="/test" component={ Test } />

      <Route
        path='/locked'
        component={ OrganizationLocked }
        onEnter={() => {

          const authenticated = new Authenticated(),
          organization = currentstore.get('organization')
          if(!authenticated.getAuthority() || authenticated.isSuper() || (organization && organization.active)) {
            browserHistory.push("/home")
          }
        }}
      />

      <Route path="/" component={ App }>
        <Route
          onEnter={() => store.dispatch(DckActionCreators.checkAuthenticated())}>

          <Route path="/home" component={ Home } />

          <Route path="/users" component={ Users }>
            <IndexRoute component={ ListUsers } />
            <Route path=":id/edit" component={ EditUser } />
            <Route path="add" component={ AddUser } />
          </Route>

          <Route path="/organizations" component={ Organizations }>
            <IndexRoute component={ ListOrganizations } />
            <Route path=":id/detail" component={ OrganizationDetail } />
            <Route path="create" component={ CreateNewOrganization } />
            <Route path=":id/update" component={ UpdateOrganization } />
          </Route>

          <Route path="/businesses" component={ Businesses }>
            <IndexRoute component={ ListBusinesses } />
            <Route path=":id/detail" component={ DetailBusiness } />
            <Route path="create" component={ CreateBusiness } />
            <Route path=":id/update" component={ UpdateBusiness } />
          </Route>

          <Route path="/customers" component={ Customers }>
            <IndexRoute component={ ListCustomers } />
            <Route path=":id/detail" component={ DetailCustomer } />
          </Route>

          <Route path="/loans" component={ Loans }>
            <IndexRoute component={ ListLoans } />
            <Route path=":id/detail" component={ DetailLoan } />
            <Route path=":id/repayment" component={ Repayment } />
            <Route path="batch-repayment" component={ BatchRepayment } />
          </Route>

          <Route path="/bundles" component={ Bundles } 
            onEnter={ redirectIfPaymentManager }>
            <IndexRoute component={ ListBundles } />
            <Route path="add" component={ AddBundle } />
            <Route path=":id/detail" component={ DetailBundle } />
            <Route path=":id/update" component={ UpdateBundle } />
          </Route>

          <Route path="/charges" component={ Charges }>
            <IndexRoute component={ ListCharges } />
            <Route path="create" component={ CreateCharge } />
            <Route path=":id/detail" component={ DetailCharge } />
            <Route path=":id/update" component={ UpdateCharge } />
          </Route>

          <Route path="/accounting" component={ Accounting }>
            <IndexRoute component={ AccountingMenu } />
          </Route>

          <Route path="/glaccounts" component={ GLAccount }>
            <IndexRoute component={ ListGLAccount } />
            <Route path="create" component={ CreateGLAccount } />
            <Route path=":id/detail" component={ GLAccountDetails } />
            <Route path=":id/update" component={ UpdateGLAccount } />
          </Route>

          <Route path="/journalentries" component={ JournalEntry }>
            <IndexRoute component={ ListJournalEntries } />
            <Route path="create" component={ CreateJournalEntry } />
            <Route path=":id/detail" component={ JournalEntryDetails } />
          </Route>

          <Route path="/glclosures" component={ GLClosure }>
            <IndexRoute component={ ListGLClosure } />
            <Route path="create" component={ CreateGLClosure } />
            <Route path=":id/detail" component={ GLClosureDetails } />
            <Route path=":id/update" component={ UpdateGLClosure } />
          </Route>

          <Route path="/accountingrules" component={ AccountingRule }>
            <IndexRoute component={ ListAccountingRules } />
            <Route path="create" component={ CreateAccountingRule } />
            <Route path=":id/detail" component={ AccountingRuleDetails } />
            <Route path=":id/update" component={ UpdateAccountingRule } />
          </Route>

          <Route path="/loancommission" component={ LoanCommission }>
            <IndexRoute component={ ListLoanCommission } />
            <Route path="create" component={ CreateLoanCommission } />
            <Route path=":id/detail" component={ LoanCommissionDetails } />
            <Route path=":id/update" component={ UpdateLoanCommission } />
          </Route>

          <Route path="/loanaccounting" component={ LoanAccounting }>
            <IndexRoute component={ LoanAccountingDetails } />
            <Route path="create" component={ CreateLoanAccounting } />
          </Route>

          <Route path="/extraaccounting" component={ ExtraAccounting }>
            <IndexRoute component={ ExtraLoanAccountingDetails } />
            <Route path="edit" component={ CreateExtraLoanAccounting } />
          </Route>

          <Route path="/customaccounting" component={ CustomAccountingRule }>
            <IndexRoute component={ CustomAccountingRuleDetails } />
            <Route path="edit" component={ CreateCustomAccountingRule } />
          </Route>

          <Route path="/defaultedloanaccounting" component={ DefaultedLoanAccounting }>
            <IndexRoute component={ DefaultedLoanAccountingDetails } />
            <Route path="edit" component={ CreateDefaultedLoanAccounting } />
          </Route>

          {/* <Route path="/vataccountingrule" component={ VATAccountingRule }>
            <IndexRoute component={ VATAccountingRuleDetails } />
            <Route path="edit" component={ CreateVATAccountingRule } />
          </Route> */}

          <Route path="/update" component={ Update } />

          <Route path="/batches" component={ Batches }>
            <IndexRoute component={ ListBatches } />
          </Route>

          <Route path="/import" component={ Import } />

          <Route path="/settings" component={ Settings }
            onEnter={ redirectIfNotSuperOrAdmin }>

            <IndexRoute component={ HomeSettings } />
            <Route path="ages" component={ AgeSettings } />
            <Route path="terms-and-conditions" component={ TermsAndConditions } />
            <Route path="loan-verification" component={ LoanVerification } />
            <Route path="customers-data" component={ CustomerDataSettings } />
            <Route path="qrcodes" component={ QRcodes } />
            <Route path="base-currency" component={ BaseCurrency } />
            <Route path="accounting" component={ AccountingSetting } />
          </Route>

          <Route path="/reports" component={ Reports } onEnter={ canISeeReports }>

            <IndexRoute component={ ReportsList } />

            <Route path="active-loans" component={ ActiveLoanReport } />
            <Route path="expected-payment-by-date" component={ ExpectedPaymentReport } />
            <Route path="balance-outstanding" component={ BalanceOutStandingReport } />
            <Route path="aging-detail" component={ AgingLoanReport } />
            <Route path="portfolio-at-risk" component={ PortfolioAtRiskReport } />
            <Route path="branch-cash-in" component={ BranchCashInReport } />
            <Route path="loan-generality" component={ LoanGeneralityReport } />
            <Route path="loans-disbursed-by-affiliation" component={ LoanDisbursedReport } />

            <Route path="general-ledger-report" component={ GeneralLedgerReport } />
            <Route path="income-statement" component={ IncomeStatementReport } />
            <Route path="balance-sheet" component={ BalanceSheetReport } />
            <Route path="trial-balance" component={ TrialBalanceReport } />

            <Route path="batch-performance" component={ BatchPerformanceReport } />
            <Route path="bucket-performance" component={ BucketPerformanceReport } />
            <Route path="defaulted-loans" component={ DefaultedLoanReport } />
          </Route>

          <Route path="/financiers" onEnter={ canISeeFinanciers }>

            <IndexRoute component={ Financiers } />

            <Route path="batches" component={ FinancierBatches } >
              <IndexRoute component={ ListFinancierBatches } />
              <Route path="create" component={ CreateFinancierBatch } />
              <Route path=":id/detail" component={ FinancierBatchDetails } />
              <Route path=":id/update" component={ UpdateFinancierBatch } />
            </Route>

            <Route path="buckets" component={ FinancierBuckets } >
              <IndexRoute component={ ListFinancierBuckets } />
              <Route path="create" component={ CreateFinancierBucket } />
              <Route path=":id/detail" component={ FinancierBucketDetails } />
              <Route path=":id/update" component={ UpdateFinancierBucket } />
            </Route>

            <Route path="investors" component={ Investors } >

              <IndexRoute component={ ListInvestors } />
              <Route path="create" component={ CreateInvestor } />
              <Route path=":id/detail" component={ InvestorDetails } />
              <Route path=":id/update" component={ UpdateInvestor } />

              <Route path="payments" component={ ListInvestorPayments } />
              <Route path="payments/new" component={ CreateInvestorPayment } />
              <Route path="payments/:id/detail" component={ InvestorPaymentDetails } />
              <Route path="payments/:id/update" component={ UpdateInvestorPayment } />
            </Route>
          </Route>

          <Route path="/my-account" component={ MyAccount } />

          <Route path="/notifications" component={ Notifications }>
            <IndexRoute component={ NotificationList } />
          </Route>

          <Route
            path="/sign-out"
            onEnter={() => store.dispatch(DckActionCreators.signOut())}
          />

          <Route path="*" component={ NotFound } />

          <IndexRedirect to="/home" />

        </Route>
      </Route>
    </Router>
  </Provider>,

  document.getElementById("root")
)

registerServiceWorker()



// WEBPACK FOOTER //
// ./src/index.js